import { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import {
  CompanyMessage,
  createCompanyMessage,
  updateCompanyMessage,
} from '../../services/dataService';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../shared/Modal';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import Button from '../shared/Button';

interface MessageModalProps {
  companyId: string;
  message: CompanyMessage | null;
  onClose: () => void;
  onSaved: () => void;
}

// Interface for our form data
interface MessageFormData {
  message: string;
  type: 'info' | 'notice' | 'warning';
  validity_range: {
    lower: string;
    upper: string;
  };
}

const MessageModal = ({ companyId, message, onClose, onSaved }: MessageModalProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<MessageFormData>({
    message: '',
    type: 'info',
    validity_range: {
      lower: DateTime.now().toFormat('yyyy-MM-dd'),
      upper: DateTime.now().plus({ days: 7 }).toFormat('yyyy-MM-dd'),
    },
  });

  useEffect(() => {
    if (message) {
      // Parse the validity_range which comes in format "[2025-04-01,2025-04-09)"
      let lower = DateTime.now().toFormat('yyyy-MM-dd');
      let upper = DateTime.now().plus({ days: 7 }).toFormat('yyyy-MM-dd');

      if (message.validity_range) {
        try {
          // Extract dates from the range format "[startDate,endDate)"
          const matches = message.validity_range.match(/\[(.*?),(.*?)\)/);
          console.info('matches', matches);
          if (matches && matches.length === 3) {
            lower = matches[1];
            // For the end date, subtract 1 day as the range is exclusive
            upper = DateTime.fromISO(matches[2]).minus({ days: 1 }).toFormat('yyyy-MM-dd');
          }
        } catch (error) {
          console.error('Error parsing validity range:', error);
        }
      }

      setFormData({
        message: message.message,
        type: message.type,
        validity_range: {
          lower,
          upper,
        },
      });
    }
  }, [message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // When sending to the API, the upper date needs to include the next day
      // since the range format is [inclusive, exclusive)
      const upperDateForApi = DateTime.fromFormat(formData.validity_range.upper, 'yyyy-MM-dd')
        .plus({ days: 1 })
        .toFormat('yyyy-MM-dd');

      // Format the validity range as a string in the format expected by the API
      const validityRangeStr = `[${formData.validity_range.lower},${formData.validity_range.upper}]`;
      console.info('caca', validityRangeStr);
      if (message) {
        await updateCompanyMessage(message.id, {
          message: formData.message,
          type: formData.type,
          validity_range: validityRangeStr,
        });
      } else {
        if (!user) throw new Error('User must be logged in to create messages');
        await createCompanyMessage(
          companyId,
          {
            message: formData.message,
            type: formData.type,
            validity_range: validityRangeStr,
          },
          user
        );
      }
      onSaved();
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={message ? t('message.edit_message') : t('message.create_new_message')}
    >
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('message.message')}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
              rows={3}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">{t('message.type')}</label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  type: e.target.value as 'info' | 'notice' | 'warning',
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="info">{t('message.info')}</option>
              <option value="notice">{t('message.notice')}</option>
              <option value="warning">{t('message.warning')}</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('message.start_date')}
            </label>
            <DatePicker
              value={DateTime.fromFormat(formData.validity_range.lower, 'yyyy-MM-dd')}
              onChange={(newValue) =>
                setFormData({
                  ...formData,
                  validity_range: {
                    ...formData.validity_range,
                    lower: newValue ? newValue.toFormat('yyyy-MM-dd') : '',
                  },
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
              format="dd/MM/yyyy"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('message.end_date')}
            </label>
            <DatePicker
              value={DateTime.fromFormat(formData.validity_range.upper, 'yyyy-MM-dd')}
              onChange={(newValue) =>
                setFormData({
                  ...formData,
                  validity_range: {
                    ...formData.validity_range,
                    upper: newValue ? newValue.toFormat('yyyy-MM-dd') : '',
                  },
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
              format="dd/MM/yyyy"
            />
          </div>
          <div className="flex justify-end space-x-3">
            <Button onClick={onClose} variant="outlined">
              {t('common.cancel')}
            </Button>
            <Button type="submit">{message ? t('common.update') : t('common.create')}</Button>
          </div>
        </form>
      </LocalizationProvider>
    </Modal>
  );
};

export default MessageModal;
